import { useCallback, useMemo, useState } from 'react';
import {
  EMPTY_YOUTUBE_SETTINGS,
  YouTubePostSettings,
  YouTubePrivacyStatus,
  youtubeDraftFromSettings,
  youtubeSettingsFromDraft,
  youtubeSettingsIssues,
  YouTubeSettingsDraft,
  YouTubeSettingsIssue,
} from 'fym-shared/src/music24/youtubePostSettings';
import { useGetYouTubePostingInfoQuery } from '../../../../../redux/api/fymAPI';

interface UseYouTubeSettingsProps {
  accountId: number;
  /** True while YouTube is selected and its channel posts through the API. */
  isActive: boolean;
}

export interface YouTubeSettingsState {
  draft: YouTubeSettingsDraft;
  issues: YouTubeSettingsIssue[];
  settings: YouTubePostSettings | null;
  /** True when nothing stops the post. */
  isReady: boolean;
  setPrivacy: (status: YouTubePrivacyStatus) => void;
  setMadeForKids: (value: boolean) => void;
  load: (stored: YouTubePostSettings | null) => void;
}

/**
 * The state of the YouTube settings section. Nothing is picked for the user: the privacy status and
 * "Made for kids" both start empty (fym-shared `youtubePostSettings.ts`).
 */
export function useYouTubeSettings({ accountId, isActive }: UseYouTubeSettingsProps): YouTubeSettingsState {
  const [draft, setDraft] = useState<YouTubeSettingsDraft>(() => ({ ...EMPTY_YOUTUBE_SETTINGS }));
  // The answer only says that the channel posts through the API (404 otherwise). Every privacy
  // status can be picked (owner finding 2026-10-10: YouTube publishes public videos of Music24).
  const query = useGetYouTubePostingInfoQuery({ accountId }, { skip: !isActive });

  const setPrivacy = useCallback(
    (status: YouTubePrivacyStatus) => setDraft((current) => ({ ...current, privacyStatus: status })),
    []
  );
  const setMadeForKids = useCallback(
    (value: boolean) => setDraft((current) => ({ ...current, madeForKids: value })),
    []
  );
  const load = useCallback((stored: YouTubePostSettings | null) => setDraft(youtubeDraftFromSettings(stored)), []);

  const issues = useMemo(() => youtubeSettingsIssues(draft), [draft]);
  const settings = useMemo(() => youtubeSettingsFromDraft(draft), [draft]);

  return {
    draft,
    issues,
    settings,
    isReady: isActive && query.data !== undefined && settings !== null && issues.length === 0,
    setPrivacy,
    setMadeForKids,
    load,
  };
}
